import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Button, Popconfirm, Skeleton, Table } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';

import { useHrCloseMutation, useLazyGetReferenceQuery } from 'app/services/edpApiV1/references';

import { uiModel } from 'entities/ui';
import { viewerModel } from 'entities/viewer';

import {
  dataObjectForReferencesTable,
  dataObjectForTableReferences,
  getErrorData,
  sortingDate,
} from 'shared/helpers/utils';
import { useNotifications } from 'shared/hooks/use-notifications';
import { useAppSelector } from 'app/store';
import { useIndicators } from 'features/indicators';
import { TPreparedReferencesData, TPreparedReferencesUserDataInfo } from 'shared/types/reference.types';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { ReferenceModal } from '../referenceModal';
import ReferencesUserForm from '../referencesUserForm';
import style from './index.module.scss';

/**
 * @page References user
 */

const ReferencesUser = () => {
  const [label, setLabel] = useState<string>();
  const [openModalRef, setOpenModalRef] = useState(false);
  const [disabledStatus, setDisabledStatus] = useState<boolean>();
  const [dataReferences, setDataReferences] = useState<TPreparedReferencesData[]>();
  const dataTable = useRef<TPreparedReferencesUserDataInfo[]>();
  const [closeHrTrigger, setHrClose] = useHrCloseMutation();

  const [getReferenceTrigger, resultReference] = useLazyGetReferenceQuery({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
  });
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const { data: reference, isLoading, error: errorGetReference } = resultReference;
  const { isSuccess: closeSuccess, isError: closeReject, reset: resetClose } = setHrClose;
  const { id } = useParams();

  const { id: profileId } = useAppSelector(viewerModel.me.selectors.selectViewerProfile);
  const { blockHrActions } = useAppSelector(uiModel.Common.selectors.selectCommon);
  const { active } = useAppSelector(uiModel.Tabs.selectors.selectTabs);

  const columns: ColumnsType<TPreparedReferencesUserDataInfo> = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'key',
      width: 120,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sorter: (a, b) => sortingDate(a.created, b.created),
    },
    {
      title: 'Вид справки',
      dataIndex: 'refTypeName',
      key: 'refTypeName',
    },
    {
      title: 'Период',
      dataIndex: 'year',
      key: 'year',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Получение',
      dataIndex: 'gettingMethod',
      key: 'gettingMethod',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <span className="d-flex">
          <Badge status={status.type} />
          {status.text}
        </span>
      ),
    },
  ];

  const rejectionReference = () => {
    if (id) {
      setOpenModalRef(true);
      setLabel('rejection');
    }
  };

  const closeReference = () => {
    if (id) {
      setLabel('close');
      closeHrTrigger({ requestId: id, allAvailableOrganizations });
    }
  };

  useEffect(() => {
    getReferenceTrigger({ requestId: Number(id), allAvailableOrganizations });
  }, []);

  useEffect(() => {
    getReferenceTrigger({ requestId: Number(id), allAvailableOrganizations });
  }, [closeSuccess]);

  useEffect(() => {
    if (reference) {
      const dataReferences = dataObjectForTableReferences(reference);
      const dataForTable = dataObjectForReferencesTable(reference);
      dataTable.current = dataForTable;
      setDataReferences(dataReferences);
    }
  }, [reference]);

  useEffect(() => {
    if (dataReferences?.length) {
      const statusText = dataReferences[0]?.status?.text;
      if (statusText === 'Изготовление') {
        setDisabledStatus(false);
        return;
      }

      if (statusText === 'Передана Hr-специалисту' && dataReferences && dataReferences[0]?.hr?.id !== profileId) {
        setDisabledStatus(true);
        return;
      }

      if (statusText === 'Передана Hr-специалисту' && dataReferences && dataReferences[0]?.hr?.id === profileId) {
        setDisabledStatus(false);
      }

      if (statusText === 'Готова к выдаче' || statusText === 'Отклонена') {
        setDisabledStatus(true);
        return;
      }

      if (statusText === 'Передана в бухгалтерию') {
        setDisabledStatus(true);
        return;
      }
    }
  }, [dataReferences]);

  useIndicators(resultReference.isSuccess, resultReference.isFetching);

  useNotifications(closeSuccess, closeReject, 'Заявление успешно закрыто', errorGetReference?.message, resetClose);

  return (
    <>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <div className={style.wrap}>
          {active !== 'myWaiting' && (
            <div className={style.btnsGroup}>
              <>
                <Popconfirm
                  placement="topRight"
                  title={'Вы уверены, что хотите отклонить заявку на справку?'}
                  onConfirm={rejectionReference}
                  cancelText="Отмена"
                  okText="Да"
                  disabled={disabledStatus || blockHrActions}
                >
                  <Button type="default" disabled={disabledStatus || blockHrActions}>
                    Отклонить
                  </Button>
                </Popconfirm>

                <Popconfirm
                  placement="topRight"
                  title={'Вы уверены что хотите закрыть заявку?'}
                  onConfirm={closeReference}
                  cancelText="Отмена"
                  okText="Да"
                  disabled={disabledStatus || blockHrActions}
                >
                  <Button type="primary" icon={<CheckOutlined />} disabled={disabledStatus || blockHrActions}>
                    Готова к выдаче
                  </Button>
                </Popconfirm>
              </>
            </div>
          )}

          <Table scroll={{ x: true }} dataSource={dataTable.current} columns={columns} pagination={false} bordered />
          {dataReferences && getReferenceTrigger && (
            <ReferencesUserForm dataReferences={dataReferences} getReferenceTrigger={getReferenceTrigger} />
          )}
        </div>
      )}

      {label && <ReferenceModal openModal={openModalRef} setOpenModal={setOpenModalRef} id={id} label={label} />}
    </>
  );
};

export default ReferencesUser;
